import { z } from 'zod';
import { lookSchema } from './look.js';

/**
 * Phone calls: voice chat between players, one to one or one to many.
 *
 * Audio never touches the server. Players connect to each other directly
 * (WebRTC, one link per pair), and the server only introduces them: it keeps
 * who is in which call, and relays the small setup messages (`voice:signal`)
 * between members. A call is with friends only, needs a registered account,
 * and never joins two players who blocked each other.
 */

/** People in one call at once (everyone connects to everyone, so this stays small). */
export const VOICE_MAX_MEMBERS = 8;
/** How long a phone rings before the call is counted as missed. */
export const VOICE_RING_MS = 40_000;
/** The biggest setup message the server will relay. */
export const VOICE_SIGNAL_MAX = 12_000;

const id = z.string().min(1).max(64);
const callId = z.string().min(1).max(64);

export const voiceClientMessageSchema = z.discriminatedUnion('t', [
  /** Ring friends. Starts a call, or adds people to the one you are in. */
  z.object({ t: z.literal('voice:call'), userIds: z.array(id).min(1).max(VOICE_MAX_MEMBERS - 1) }),
  /** Pick up or decline a ringing call. */
  z.object({ t: z.literal('voice:answer'), callId, accept: z.boolean() }),
  /** Hang up. */
  z.object({ t: z.literal('voice:leave'), callId }),
  /** A connection setup message for one other member (an offer or an answer). */
  z.object({ t: z.literal('voice:signal'), callId, to: id, data: z.string().min(1).max(VOICE_SIGNAL_MAX) }),
]);

const personSchema = z.object({ id: z.string(), displayName: z.string(), look: lookSchema });

export const voiceServerMessageSchema = z.discriminatedUnion('t', [
  /** Your phone is ringing. */
  z.object({ t: z.literal('voice:invite'), callId: z.string(), from: personSchema }),
  /**
   * Who is in the call now and who is still ringing. `joined` names the member
   * who just arrived: everyone who was already there sets up a link to them.
   */
  z.object({
    t: z.literal('voice:state'),
    callId: z.string(),
    members: z.array(personSchema),
    ringing: z.array(personSchema),
    joined: z.string().nullable(),
  }),
  z.object({ t: z.literal('voice:signal'), callId: z.string(), from: z.string(), data: z.string() }),
  /** Your part in the call is over (hung up, declined, missed, or everyone left). */
  z.object({
    t: z.literal('voice:ended'),
    callId: z.string(),
    reason: z.enum(['ended', 'declined', 'missed', 'busy', 'unavailable']),
  }),
  /** Someone in your room picked up or put down their phone (it shows in their hand). */
  z.object({ t: z.literal('voice:status'), userId: z.string(), calling: z.boolean() }),
  /** On entering a room: everyone in it who is on a call right now. */
  z.object({ t: z.literal('voice:roomstatus'), calling: z.array(z.string()) }),
]);

export type VoiceClientMessage = z.infer<typeof voiceClientMessageSchema>;
export type VoiceServerMessage = z.infer<typeof voiceServerMessageSchema>;

/** ICE servers the browser uses to find a path between two players. */
export interface VoiceConfig {
  iceServers: Array<{ urls: string | string[]; username?: string; credential?: string }>;
}
